/**
 * [BLURY] | 🥭 exportComposition
 * Renders the composition once more at full output resolution and encodes it.
 * The preview canvas is capped, so this is a separate render rather than a
 * read-back — that is what makes an arbitrary export width possible.
 * @build 13.09.26
 * @updated 13.09.26 PHT
 * @author TONYTONY Sàrl
 */
import { AVIF_MIME, encodeAvif } from './avif';
import { GrainFieldRenderer, scaleComposition } from './renderer';
import type { Composition, ExportFormat, Size } from './types';

const MIME: Record<ExportFormat, string> = {
  png: 'image/png',
  avif: AVIF_MIME,
};

export const FORMAT_LABEL: Record<ExportFormat, string> = {
  png: 'PNG',
  avif: 'AVIF',
};

/** Formats that carry a quality setting. */
export function isLossy(format: ExportFormat): boolean {
  return format === 'avif';
}

function canvasToBlob(canvas: HTMLCanvasElement, mime: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => (blob ? resolve(blob) : reject(new Error('the canvas produced no image data'))),
      mime,
      quality,
    );
  });
}

export interface ExportRequest {
  composition: Composition;
  /** Full output size, not the preview size. */
  size: Size;
  seed: number;
  /** How much bigger the output is than the preview the composition was tuned on. */
  scale: number;
  format: ExportFormat;
  /** 0–1, used by lossy formats only. */
  quality: number;
}

export interface ExportResult {
  blob: Blob;
  filename: string;
}

export async function exportComposition(request: ExportRequest): Promise<ExportResult> {
  const { composition, size, seed, scale, format, quality } = request;

  const canvas = document.createElement('canvas');
  canvas.width = size.width;
  canvas.height = size.height;

  // A fresh renderer keeps this off the preview's cached grain tile.
  new GrainFieldRenderer().render(canvas, scaleComposition(composition, scale), size, seed);

  const blob = await encodeCanvas(canvas, size, format, quality);
  return { blob, filename: `grain-field-${size.width}x${size.height}.${format}` };
}

async function encodeCanvas(
  canvas: HTMLCanvasElement,
  size: Size,
  format: ExportFormat,
  quality: number,
): Promise<Blob> {
  if (format === 'png') return canvasToBlob(canvas, MIME.png);

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('the export canvas lost its context');
  return encodeAvif(ctx.getImageData(0, 0, size.width, size.height), Math.round(quality * 100));
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
